import {
    CheckCircleIcon,
    ClockIcon,
    GiftIcon,
    NoSymbolIcon,
    Squares2X2Icon
} from '@heroicons/react/24/outline';
import { StatCard } from './StatusCards';
import { formatMoneyDollars } from '../../utils/money';
import type { OneTimeDiscountStatusSummary } from '../../services/billing';

// Clickable status stat cards above the one-time discounts table. Unlike the
// charge cards these lead with the dollar total (the design's metric tiles
// folded into the filter cards), with the one-time discount count as the
// unit line. The summary comes from the list endpoint and respects the
// practice/search filters but not the status filter, so numbers stay stable
// while filtering.

interface CardConfig {
    status: string | null; // null = all one-time discounts
    label: string;
    icon: React.ReactNode;
}

const ICON_CLASS = 'h-3.5 w-3.5';

const CARD_CONFIGS: CardConfig[] = [
    {
        status: null,
        label: 'All one-time discounts',
        icon: <Squares2X2Icon className={ICON_CLASS} />
    },
    { status: 'available', label: 'Available', icon: <GiftIcon className={ICON_CLASS} /> },
    { status: 'expiring', label: 'Expiring soon', icon: <ClockIcon className={ICON_CLASS} /> },
    { status: 'applied', label: 'Applied', icon: <CheckCircleIcon className={ICON_CLASS} /> },
    { status: 'expired', label: 'Expired', icon: <NoSymbolIcon className={ICON_CLASS} /> }
];

interface OneTimeDiscountStatusCardsProps {
    statusSummary: Record<string, OneTimeDiscountStatusSummary>;
    selected: string[];
    onChange: (next: string[]) => void;
}

export function OneTimeDiscountStatusCards({
    statusSummary,
    selected,
    onChange
}: OneTimeDiscountStatusCardsProps) {
    const entries = Object.values(statusSummary);
    const allCount = entries.reduce((sum, e) => sum + e.count, 0);
    const allTotal = entries.reduce((sum, e) => sum + Number(e.totalAmount), 0);

    return (
        <div className="flex items-stretch gap-3 px-8 pt-4">
            {CARD_CONFIGS.map((config) => {
                const isActive =
                    config.status === null
                        ? selected.length === 0
                        : selected.length === 1 && selected[0] === config.status;
                const entry =
                    config.status === null
                        ? { count: allCount, total: allTotal }
                        : {
                              count: statusSummary[config.status]?.count ?? 0,
                              total: Number(statusSummary[config.status]?.totalAmount ?? 0)
                          };
                return (
                    <StatCard
                        key={config.status ?? 'all'}
                        label={config.label}
                        icon={config.icon}
                        value={formatMoneyDollars(entry.total)}
                        unit={`${entry.count} one-time discount${entry.count === 1 ? '' : 's'}`}
                        isActive={isActive}
                        onClick={() => {
                            if (config.status === null || isActive) {
                                onChange([]);
                            } else {
                                onChange([config.status]);
                            }
                        }}
                    />
                );
            })}
        </div>
    );
}
